import type { ImageAnalysis, RegionStat, Rect, ZoneScore, FaceBox } from '@/types';
import { extractPalette, luminance, rgbToHex } from '@/lib/color';
import { clamp, overlapRatio } from '@/lib/utils';
import { detectFaces } from './faceDetector';

/**
 * Local image analysis. Runs entirely in the browser on a downsampled copy, so
 * it works with no API key and adds no latency to the design loop. The vision
 * model (when configured) refines these numbers; it never replaces them.
 */

const SAMPLE_EDGE = 128;
const GRID_COLS = 12;
const GRID_ROWS = 12;

function sampleImage(img: HTMLImageElement): {
  data: Uint8ClampedArray;
  width: number;
  height: number;
} {
  const scale = SAMPLE_EDGE / Math.max(img.naturalWidth, img.naturalHeight);
  const w = Math.max(8, Math.round(img.naturalWidth * scale));
  const h = Math.max(8, Math.round(img.naturalHeight * scale));
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) throw new Error('Canvas 2D is unavailable in this browser.');
  ctx.drawImage(img, 0, 0, w, h);
  const { data } = ctx.getImageData(0, 0, w, h);
  return { data, width: w, height: h };
}

function buildGrid(
  data: Uint8ClampedArray,
  w: number,
  h: number,
): { cols: number; rows: number; cells: RegionStat[] } {
  const cells: RegionStat[] = [];
  for (let row = 0; row < GRID_ROWS; row++) {
    for (let col = 0; col < GRID_COLS; col++) {
      const x0 = Math.floor((col / GRID_COLS) * w);
      const x1 = Math.max(x0 + 1, Math.floor(((col + 1) / GRID_COLS) * w));
      const y0 = Math.floor((row / GRID_ROWS) * h);
      const y1 = Math.max(y0 + 1, Math.floor(((row + 1) / GRID_ROWS) * h));

      let sumL = 0;
      let sumL2 = 0;
      let sr = 0;
      let sg = 0;
      let sb = 0;
      let n = 0;

      for (let y = y0; y < y1; y++) {
        for (let x = x0; x < x1; x++) {
          const i = (y * w + x) * 4;
          const r = data[i];
          const g = data[i + 1];
          const b = data[i + 2];
          const l = luminance({ r, g, b });
          sumL += l;
          sumL2 += l * l;
          sr += r;
          sg += g;
          sb += b;
          n++;
        }
      }

      const mean = n ? sumL / n : 0;
      const variance = n ? Math.max(0, sumL2 / n - mean * mean) : 0;
      cells.push({
        col,
        row,
        luminance: mean,
        detail: Math.sqrt(variance),
        color: rgbToHex({ r: sr / n, g: sg / n, b: sb / n }),
      });
    }
  }
  return { cols: GRID_COLS, rows: GRID_ROWS, cells };
}

/**
 * Saliency proxy: gradient energy (edges) plus deviation from the frame's mean
 * luminance. Cheap, deterministic, and good enough to find "where the stuff is"
 * — which is all the layout engine needs.
 */
function saliencyMap(
  data: Uint8ClampedArray,
  w: number,
  h: number,
  meanL: number,
): Float32Array {
  const lum = new Float32Array(w * h);
  for (let i = 0, p = 0; i < data.length; i += 4, p++) {
    lum[p] = luminance({ r: data[i], g: data[i + 1], b: data[i + 2] });
  }
  const sal = new Float32Array(w * h);
  for (let y = 1; y < h - 1; y++) {
    for (let x = 1; x < w - 1; x++) {
      const p = y * w + x;
      const gx =
        -lum[p - w - 1] - 2 * lum[p - 1] - lum[p + w - 1] +
        lum[p - w + 1] + 2 * lum[p + 1] + lum[p + w + 1];
      const gy =
        -lum[p - w - 1] - 2 * lum[p - w] - lum[p - w + 1] +
        lum[p + w - 1] + 2 * lum[p + w] + lum[p + w + 1];
      const edge = Math.sqrt(gx * gx + gy * gy);
      sal[p] = edge * 0.75 + Math.abs(lum[p] - meanL) * 0.25;
    }
  }
  return sal;
}

function focalAndSubject(
  sal: Float32Array,
  w: number,
  h: number,
): { focal: { x: number; y: number }; subject: Rect } {
  let total = 0;
  let cx = 0;
  let cy = 0;
  let peak = 0;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const v = sal[y * w + x];
      total += v;
      cx += x * v;
      cy += y * v;
      if (v > peak) peak = v;
    }
  }
  const focal =
    total > 0
      ? { x: cx / total / w, y: cy / total / h }
      : { x: 0.5, y: 0.5 };

  // Subject box = the tight bounds of cells above ~45% of peak saliency, which
  // reliably captures a person or product without swallowing the whole frame.
  const threshold = peak * 0.45;
  let minX = w;
  let maxX = 0;
  let minY = h;
  let maxY = 0;
  let hits = 0;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (sal[y * w + x] >= threshold) {
        hits++;
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
  }
  const subject: Rect =
    hits > 0
      ? {
          x: minX / w,
          y: minY / h,
          w: (maxX - minX + 1) / w,
          h: (maxY - minY + 1) / h,
        }
      : { x: 0.25, y: 0.25, w: 0.5, h: 0.5 };

  return { focal, subject };
}

/** Candidate placements, in the order a designer would consider them. */
const ZONE_DEFS: { id: string; label: string; rect: Rect }[] = [
  { id: 'bottom', label: 'Lower third', rect: { x: 0.06, y: 0.6, w: 0.88, h: 0.34 } },
  { id: 'top', label: 'Upper third', rect: { x: 0.06, y: 0.06, w: 0.88, h: 0.3 } },
  { id: 'left', label: 'Left column', rect: { x: 0.06, y: 0.18, w: 0.44, h: 0.64 } },
  { id: 'right', label: 'Right column', rect: { x: 0.5, y: 0.18, w: 0.44, h: 0.64 } },
  { id: 'center', label: 'Center', rect: { x: 0.12, y: 0.32, w: 0.76, h: 0.36 } },
  { id: 'top-left', label: 'Top left', rect: { x: 0.06, y: 0.07, w: 0.5, h: 0.3 } },
  { id: 'top-right', label: 'Top right', rect: { x: 0.44, y: 0.07, w: 0.5, h: 0.3 } },
  { id: 'bottom-left', label: 'Bottom left', rect: { x: 0.06, y: 0.62, w: 0.52, h: 0.31 } },
  { id: 'bottom-right', label: 'Bottom right', rect: { x: 0.42, y: 0.62, w: 0.52, h: 0.31 } },
];

function statsForRect(
  grid: { cols: number; rows: number; cells: RegionStat[] },
  rect: Rect,
): { luminance: number; detail: number } {
  let sumL = 0;
  let sumD = 0;
  let n = 0;
  for (const cell of grid.cells) {
    const cx = (cell.col + 0.5) / grid.cols;
    const cy = (cell.row + 0.5) / grid.rows;
    if (cx >= rect.x && cx <= rect.x + rect.w && cy >= rect.y && cy <= rect.y + rect.h) {
      sumL += cell.luminance;
      sumD += cell.detail;
      n++;
    }
  }
  return n ? { luminance: sumL / n, detail: sumD / n } : { luminance: 0.5, detail: 0.2 };
}

function scoreZones(
  grid: { cols: number; rows: number; cells: RegionStat[] },
  faces: FaceBox[],
  subject: Rect,
  focal: { x: number; y: number },
): ZoneScore[] {
  return ZONE_DEFS.map((def) => {
    const { luminance: lum, detail } = statsForRect(grid, def.rect);

    // Measured as the fraction of the FACE the zone covers, not the fraction
    // of the zone the face occupies. The latter reads near-zero for a small
    // face in a large zone — precisely the case where text lands on it.
    const faceOverlap = faces.reduce(
      (max, f) => Math.max(max, overlapRatio(f, def.rect)),
      0,
    );
    const subjectOverlap = overlapRatio(def.rect, subject);

    const focalDist = Math.hypot(
      focal.x - (def.rect.x + def.rect.w / 2),
      focal.y - (def.rect.y + def.rect.h / 2),
    );

    const calm = 1 - clamp(detail * 3.2, 0, 1);
    const score =
      calm * 0.42 +
      clamp(focalDist * 1.5, 0, 1) * 0.18 +
      (1 - subjectOverlap) * 0.2 +
      (1 - faceOverlap) * 0.2 -
      faceOverlap * 0.45;

    return {
      id: def.id,
      label: def.label,
      rect: def.rect,
      luminance: lum,
      detail,
      score,
      faceOverlap,
      subjectOverlap,
    };
  }).sort((a, b) => b.score - a.score);
}

export async function analyzeImage(img: HTMLImageElement): Promise<ImageAnalysis> {
  const sample = sampleImage(img);
  const grid = buildGrid(sample.data, sample.width, sample.height);

  const meanL =
    grid.cells.reduce((s, c) => s + c.luminance, 0) / grid.cells.length;
  const lums = grid.cells.map((c) => c.luminance);
  const contrastSpread = Math.max(...lums) - Math.min(...lums);
  const meanDetail =
    grid.cells.reduce((s, c) => s + c.detail, 0) / grid.cells.length;

  const sal = saliencyMap(sample.data, sample.width, sample.height, meanL);
  const { focal, subject } = focalAndSubject(sal, sample.width, sample.height);
  const faces = await detectFaces(img, sample);
  const zones = scoreZones(grid, faces, subject, focal);
  const dominantColors = extractPalette(sample.data, 5);

  const sortedByLum = [...zones].sort((a, b) => b.luminance - a.luminance);

  return {
    width: img.naturalWidth,
    height: img.naturalHeight,
    aspect: img.naturalWidth / img.naturalHeight,
    grid,
    dominantColors,
    averageLuminance: meanL,
    contrastSpread,
    focalPoint: focal,
    subject,
    faces,
    zones,
    brightestZone: sortedByLum[0]?.id ?? 'top',
    darkestZone: sortedByLum[sortedByLum.length - 1]?.id ?? 'bottom',
    busy: meanDetail > 0.16 || contrastSpread > 0.55,
  };
}

/** Mean color of a zone, for contrast checks against the real background. */
export function zoneColor(analysis: ImageAnalysis, rect: Rect): string {
  const { grid } = analysis;
  let sr = 0;
  let sg = 0;
  let sb = 0;
  let n = 0;
  for (const cell of grid.cells) {
    const cx = (cell.col + 0.5) / grid.cols;
    const cy = (cell.row + 0.5) / grid.rows;
    if (cx >= rect.x && cx <= rect.x + rect.w && cy >= rect.y && cy <= rect.y + rect.h) {
      const hex = cell.color.replace('#', '');
      sr += parseInt(hex.slice(0, 2), 16);
      sg += parseInt(hex.slice(2, 4), 16);
      sb += parseInt(hex.slice(4, 6), 16);
      n++;
    }
  }
  if (!n) return '#808080';
  return rgbToHex({ r: sr / n, g: sg / n, b: sb / n });
}

export function findZone(analysis: ImageAnalysis, id: string): ZoneScore {
  return analysis.zones.find((z) => z.id === id) ?? analysis.zones[0];
}
